import Link from 'next/link';
import { Icon } from '@geberit/gdds';
import key from 'weak-key';

// styles
import styles from './breadcrumb-item.module.scss';

// utils
import { breadcrumbTrackingAction } from 'components/Navigation/trackingActions';
import { useTracking } from 'utils/hooks/useTracking';
import { decodingContent } from 'utils/decodingContent';

type BreadcrumbItemProps = Readonly<Partial<Breadcrumb>>;

export function BreadcrumbItem(props: BreadcrumbItemProps) {
  const track = useTracking();
  const { siblings, dots, isHome, isLast, url, label, type, ...itemProps } = props;

  const getOnClickHandler = (label) => () => {
    track.trackEvent(breadcrumbTrackingAction(label));
  };

  if (isHome) {
    return (
      <li className={styles['breadcrumb-item']}>
        <Link
          href={url ?? ''}
          className={styles['home']}
          {...itemProps}
          onClick={getOnClickHandler(label)}
        >
          <Icon symbol="home" />
        </Link>

        <Icon symbol="chevron_right_flat" />
      </li>
    );
  }

  if (dots) {
    return (
      <li className={styles['breadcrumb-item']}>
        <button className={styles['dots']} {...itemProps}>
          <Icon symbol="truncation_pagination" />
        </button>
        <Icon symbol="chevron_right_flat" />
      </li>
    );
  }

  if (siblings && siblings.length > 1) {
    return (
      <li className={styles['breadcrumb-item']}>
        <button className={styles.trigger} data-role="trigger" {...itemProps}>
          {label}
          <Icon symbol="chevron_down" aria-hidden="true" />
        </button>
        <div className={styles['popover']}>
          {siblings.map((sibling, index) => {
            return (
              <Link
                onClick={getOnClickHandler(sibling.label)}
                href={sibling.url ?? ''}
                key={key(sibling)}
                {...(index === 0 ? { 'aria-disabled': true, tabIndex: -1 } : {})}
              >
                {decodingContent(sibling.label)}
              </Link>
            );
          })}
        </div>
        {!isLast && <Icon symbol="chevron_right_flat" />}
      </li>
    );
  }

  return (
    <li className={styles['breadcrumb-item']}>
      <Link href={url ?? ''} {...itemProps} onClick={getOnClickHandler(label)}>
        {decodingContent(label)}
      </Link>
      {!isLast && <Icon symbol="chevron_right_flat" />}
    </li>
  );
}
